/**
 * Shared date formatting utilities for Crew Forge.
 *
 * NEVER define local formatDate / formatDateTime / formatDateShort functions.
 * Always import from this module.
 *
 * All functions append 'T00:00:00' to date-only strings to prevent timezone shifts.
 */

/**
 * Normalise a date string to a Date object.
 * Handles both "YYYY-MM-DD" and ISO datetime strings.
 * Appends T00:00:00 to date-only strings to avoid timezone issues.
 */
function parseDate(dateStr) {
  if (!dateStr) return null;
  const dateOnly = String(dateStr).split('T')[0];
  const d = new Date(dateOnly + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date string as UK format: "26 Mar 2026"
 * Returns '-' for null/undefined/invalid dates.
 * Uses day: '2-digit', month: 'short', year: 'numeric'.
 */
export function formatDate(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return '-';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Format a date string with time: "26 Mar 2026, 14:30"
 * Returns '-' for null/undefined/invalid dates.
 */
export function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  // For datetime strings, preserve the original time
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * 🪤 **NOT COMPACT, DESPITE THE NAME. This is a straight alias of `formatDate`
 * and returns a FOUR-digit year: "26 Mar 2026".**
 *
 * If you came here looking for something that fits a narrow column, you want
 * **`formatDateCompact`** below — "26 Mar 26". This one saves nothing: it is
 * byte-for-byte `formatDate`, and its old docblock ("Compact date format for
 * tight spaces") actively misled, which is why the wording is now the warning.
 *
 * It is kept, rather than deleted or redefined, because four live callers use it
 * (`CrewGanttView`, `CompareViewBody`, `CalendarView`, `TaskCard`) and none of
 * them asked to be reformatted — quietly changing the format underneath them is
 * a UI change disguised as a tidy-up. Prefer `formatDate` in new code; reach for
 * this only when you are matching an existing call site.
 *
 * Returns '-' for null/undefined/invalid dates.
 */
export function formatDateShort(dateStr) {
  return formatDate(dateStr);
}

/**
 * Genuinely compact date for a narrow table column: **"25 Apr 26"**.
 *
 * Product owner, 2026-08-08, for the order table's Date column — he wrote the
 * format out himself, so the two-digit year is the request, not a liberty.
 *
 * ⚠️ **`formatDateShort` is NOT this and never was**, despite its name and its
 * docblock ("Compact date format for tight spaces"). It is a straight alias of
 * `formatDate` and returns a four-digit year — the two functions are identical,
 * which is why a new one was added rather than the alias being "fixed". Changing
 * `formatDateShort` would silently reformat every one of its callers
 * (`CrewGanttView`, `CompareViewBody`, `CalendarView`, `TaskCard`), none of which
 * asked for it.
 *
 * The saving is the whole point and it is measured, not assumed: at `text-sm` in
 * the app's font, "25 Apr 26" is **65.45px** against **82.81px** for
 * "25 Apr 2026" — **17.36px per row**, which is the difference between a Date
 * column that fits in 90px and one that needs 110.
 *
 * Same `parseDate` as everything else here, so a date-only string still gets
 * `T00:00:00` appended and cannot shift a day across a timezone boundary.
 * Returns '-' for null/undefined/invalid, matching `formatDate`.
 *
 * 🚨 **IT CANNOT CARRY A TIME, AND THAT IS INVISIBLE FROM THIS SIGNATURE — hand
 * it a datetime and the hour is SILENTLY DISCARDED.** `parseDate` does
 * `String(dateStr).split('T')[0]`, so the time is gone before any formatting
 * happens and **no combination of options will get it back**. The saving here is
 * the two-digit YEAR, never the time. Reach for **`formatDateTimeCompact`**
 * below, which parses the string whole for exactly this reason.
 *
 * ⚠️ Written at BOTH sites deliberately. The trap was recorded only in
 * `formatDateTimeCompact`'s docblock, which is the one place a reader who
 * already knows about it looks — whereas the person about to lose an hour is
 * reaching for THIS function to shrink a timestamp and has no reason to scroll
 * down. Repeating one sentence is cheaper than the bug.
 */
export function formatDateCompact(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return '-';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

/**
 * Day and month only: **"25 Apr"** (2026-09-30, the My Orders PHONE table's
 * Job cell, whose second line carries the order date in a ~100px column).
 * Same `parseDate` as `formatDateCompact`, so a date-only string cannot shift a
 * day across a timezone. 🪤 No year at all — use it only where the row says
 * which year elsewhere or where a same-season list makes it obvious; the
 * expanded panel still carries the full date. Returns '-' for null/invalid.
 */
export function formatDateDayMonth(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return '-';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Weekday, day and month: **"Fri 9 Oct"** (2026-10-02, My Tasks' "View by
 * Shoot date" day headers). Same `parseDate` as its siblings, so a date-only
 * string cannot shift a day across a timezone. No year — a shoot-day header
 * is always within the coming weeks. Returns '-' for null/invalid.
 */
export function formatDateWeekdayDayMonth(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return '-';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');
}

/**
 * Compact date **with the time kept**: **"7 May 26, 01:00"**.
 *
 * Added 2026-08-10 for the approval stepper, whose per-step timestamp moved onto
 * the same line as the approver's name (product owner: the history log is *"way
 * to chunky and takes up a lot of room"*).
 *
 * 🚨 **`formatDateCompact` CANNOT BE USED FOR THIS AND THE REASON IS NOT
 * OBVIOUS.** It runs its input through the shared `parseDate`, which does
 * `String(dateStr).split('T')[0]` — i.e. it DISCARDS THE TIME before formatting.
 * So `formatDateCompact` can never emit an hour whatever options you hand it,
 * and reaching for it here would silently delete the time from every decided
 * step. This function follows `formatDateTime` instead and parses the string
 * whole, which is what preserves it.
 *
 * ⚠️ **The trade-off that makes it acceptable is the YEAR, never the time.**
 * `formatDateTime` gives "07 May 2026, 01:00"; this gives "7 May 26, 01:00" —
 * a two-digit year and a non-padded day. The two-digit year is the same saving
 * `formatDateCompact` was added for and which is measured in its docblock
 * (17.36px at `text-sm`); the time is INFORMATION and stays.
 *
 * Returns '-' for null/undefined/invalid, matching every other function here.
 */
export function formatDateTimeCompact(dateStr) {
  if (!dateStr) return '-';
  // NOT `parseDate` — see the docblock. That helper strips the time.
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date string, returning null (not '-') for empty values.
 * Useful when callers need to distinguish missing dates from formatted ones
 * (e.g., OrderPage where callers append fallbacks like '|| "N/A"').
 */
export function formatReadableDate(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return null;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Numeric UK date, no month name: **"15/08/26"** (dd/mm/yy).
 *
 * Added 2026-08-17 for the Labour Flow status-icon tooltip (`UserStatusIndicator`'s
 * `sinceDate` prop) — the owner wrote the format himself: *"invite sent on
 * 15/08/26 - 2 days ago"*. None of the date-with-month-name formats above
 * (`formatDate`, `formatDateCompact`, …) match that; this is a distinct
 * export rather than a fourth reformat of one of them, for the same reason
 * `formatDateCompact` didn't reuse `formatDateShort`'s name — a shared name
 * for two different outputs is how a future caller gets silently reformatted.
 *
 * 🚨 **Deliberately NOT built on `parseDate`.** Its input here is always a
 * full DATETIME column (`users.created_at` / `invitations.created_at`), and
 * `parseDate` assumes a 'T' separator to strip the time — feed it a SQLite
 * `CURRENT_TIMESTAMP` value ("2026-08-15 10:23:00", a SPACE, not a 'T') and
 * `.split('T')[0]` returns the string unchanged, so appending `'T00:00:00'`
 * produces an invalid Date. Same reasoning as `formatDateTime` /
 * `formatDateTimeCompact` just above: for a real timestamp, `new Date(dateStr)`
 * handles both the space- and 'T'-separated shapes correctly, and there is no
 * calendar-day to protect from a UTC shift because the value already carries
 * a real time component. Never feed this a date-ONLY string ("2026-08-15")
 * expecting date-only-string safety — it has none, on purpose, because its
 * only two callers today are timestamp columns.
 *
 * Returns '-' for null/undefined/invalid, matching every other formatter here.
 */
export function formatDateNumeric(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/**
 * Coarse relative age, spelled out in full: **"2 days ago"**, "5 minutes
 * ago", "just now", "3 weeks ago", "4 months ago", "2 years ago".
 *
 * Added 2026-08-17 for the same tooltip as `formatDateNumeric` — the owner's
 * own wording, singular/plural handled ("1 day ago", never "1 days ago").
 *
 * ⚠️ **NOT the same as `Layout.js`'s notification-bell `formatTime`**
 * (`"2d ago"`, abbreviated) or `TaskCommentThread.js`'s inline copy of it —
 * both stay exactly as they are; neither was asked to change, and the owner's
 * example for THIS feature spells the unit out. A shared helper was added
 * here rather than a third inline copy of the notification bell's version,
 * per CLAUDE.md's "extract before duplicating a third time" — but it is a
 * NEW export with different wording, not a refactor of the other two, so
 * their callers are unaffected.
 *
 * Takes a full timestamp (`new Date(dateStr)`, same reasoning as
 * `formatDateNumeric` above — never `parseDate`, which would discard the
 * time this function measures against `Date.now()`).
 *
 * Returns '' for null/undefined/invalid — deliberately not '-', so a caller
 * building a compound string ("Invite sent on 15/08/26 — ") can test
 * truthiness and omit the em-dash clause entirely rather than print a bare
 * dash after it.
 */
export function formatRelativeAge(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;

  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return plural(diffMins, 'minute');

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return plural(diffHours, 'hour');

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return plural(diffDays, 'day');

  const diffWeeks = Math.floor(diffDays / 7);
  if (diffDays < 30) return plural(diffWeeks, 'week');

  const diffMonths = Math.floor(diffDays / 30);
  if (diffDays < 365) return plural(diffMonths, 'month');

  const diffYears = Math.floor(diffDays / 365);
  return plural(diffYears, 'year');
}

/**
 * Return YYYY-MM-DD for a Date in *local* time.
 *
 * ALWAYS use this when you need the "date-only" string that represents the
 * user's local calendar day. Deriving the same string from a UTC conversion
 * (ISO-string slice of the first ten chars) silently shifts the date back
 * by one in any UTC+N timezone around midnight — causing red-dot / event-count
 * mismatches, off-by-one "today" comparisons, and late-night overdue false
 * positives. The regression guard in
 * `server/tests/regressions/no-utc-date-drift.test.js` enforces this.
 */
export function toLocalISO(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
